import React, { useState } from 'react'
import { analytics } from 'Modules/Analytics'
import { useAppState } from 'Context/AppContext/AppContext'
import { useMutation, useQuery } from '@apollo/client'
import moment, { Moment } from 'moment'
import { CHART } from 'Apollo/Queries/Chart'
import { CHART_CATEGORY } from 'Apollo/Queries/ChartCategory'
import { CREATE_CHART_DATA_POINTS } from 'Apollo/Mutations/CreateChartDataPoints'
import {
  ChartCategory,
  ChartDataPoint,
  ChartDataPointInput,
} from 'Generated/graphql-types'
import {
  Table,
  TableHead,
  TableBody,
  TablePagination,
  Snackbar,
} from '@mui/material'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import {
  ChartDateCell,
  ChartTableCell,
  ChartTableRow,
  EditChartDataTableContainer,
} from './EditChartDataTable.styled'
import { EditChartDataTableRow } from './EditChartDataTableRow'
import { filterByDateRange, MM_DD_YYYY } from '../utils'
import { CreateNewEntryRow } from './CreateNewEntryRow'
import { GOALS } from '../../../../../Apollo/Queries/Goals'

type EditChartDataTableProps = {
  goalId: string
  filterEndDate: Moment | null
  filterStartDate: Moment | null
  isAddingData: boolean
  onEditData?: (row: {
    date: string
    values: (Omit<ChartDataPoint, 'id'> & { id: number | null })[]
  }) => void
  isGoalSharedWithFunder: boolean
  newEntryDate: Moment | null
  onAddData: () => void
  onChangeNewEntryDate: (date: Moment | null) => void
  simple?: boolean
}

export const EditChartDataTable = ({
  goalId,
  filterEndDate,
  filterStartDate,
  isAddingData,
  isGoalSharedWithFunder,
  newEntryDate,
  onAddData,
  onChangeNewEntryDate,
  onEditData,
  simple,
}: EditChartDataTableProps) => {
  const {
    nonprofitOrgConfig: { role, nonprofitOrgRole },
  } = useAppState()
  const [newEntryValues, setNewEntryValues] = useState<
    Record<number, ChartDataPointInput>
  >({})
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const { nonprofitOrgConfig } = useAppState()
  const { orgId: nonprofitOrgId } = nonprofitOrgConfig

  const { data: { chart = {} } = {} } = useQuery(CHART, {
    variables: { goalId: parseInt(goalId, 10) },
  })

  const { data: { chartCategory = [] } = {} } = useQuery(CHART_CATEGORY, {
    variables: { chartId: parseInt(chart?.id, 10) },
    onCompleted: (data) => resetNewEntryValues(data.chartCategory),
    skip: !chart?.id,
  })

  const [createChartDataPoints] = useMutation(CREATE_CHART_DATA_POINTS, {
    refetchQueries: [
      { query: CHART_CATEGORY, variables: { chartId: chart.id } },
    ],
    awaitRefetchQueries: true,
  })

  const resetNewEntryValues = (categories: ChartCategory[]) => {
    setNewEntryValues({
      ...categories.reduce(
        (acc: Record<number, ChartDataPointInput>, category: ChartCategory) => {
          acc[category.id] = {
            chartId: chart.id,
            chartCategoryId: category.id,
            date: null,
            value: null,
          }
          return acc
        },
        {}
      ),
    })
  }

  const popToast = (message: string) => {
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  const handleCloseSnackbar = () => {
    setShowSnackbar(false)
    setSnackbarMessage('')
  }

  const handleCloseNewEntry = () => {
    onChangeNewEntryDate(moment.utc())
    resetNewEntryValues(chartCategory)
    onAddData()
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleValueChange = (
    newValue: string,
    point: {
      chartCategoryId: number
      chartId: number
    }
  ) => {
    const { chartCategoryId, chartId } = point
    if (!chartCategoryId || !chartId || !newEntryDate) return

    const updatedPoint = {
      chartId,
      chartCategoryId,
      date: null,
      value: parseFloat(newValue) || null,
    }

    setNewEntryValues({ ...newEntryValues, [chartCategoryId]: updatedPoint })
  }

  const handleCreateDataPoints = async () => {
    const dataPoints = Object.values(newEntryValues).map((point) => ({
      ...point,
      // Ensure latest user-selected date is used right before submit
      date: newEntryDate?.toISOString(),
    }))

    const hasValidDates = dataPoints.every((point) => point.date)

    if (!hasValidDates) {
      popToast('Please select a date before submitting.')
      return
    }

    try {
      await createChartDataPoints({
        variables: { data: dataPoints },
        refetchQueries: [
          { query: CHART, variables: { goalId: parseInt(goalId, 10) } },
          { query: CHART_CATEGORY, variables: { chartId: chart.id } },
          {
            query: GOALS,
            variables: {
              activeOnly: true,
              includeOnboarding: true,
              nonprofitOrgId,
            },
          },
        ],
      })
      popToast('You’ve added a new data point successfully')
      handleCloseNewEntry()
      analytics.trackActionCreator(goals.Create_Data_Point_Submitted)({
        goalId,
        isGoalSharedWithFunder,
        role,
        relationship: nonprofitOrgRole,
      })
    } catch (e) {
      popToast(
        'There was an error updating your data points. Check if the date entered already exists.'
      )
    }
  }

  // Sort category IDs in ascending order
  const sortedCategoryData = [...chartCategory].sort((a, b) => a.id - b.id)
  const categoryHeaders = sortedCategoryData.map((category: ChartCategory) => (
    <ChartTableCell key={category.title}>{category.title}</ChartTableCell>
  ))

  const rowDataMap: Record<string, Record<number, ChartDataPoint>> = {}

  // Populate the data map with active data points for N categories
  sortedCategoryData.forEach((category: ChartCategory) => {
    category.ChartDataPoint.filter((point) => !point.archivedAt).forEach(
      (point) => {
        const date = moment.utc(point.date).format(MM_DD_YYYY)
        if (!rowDataMap[date]) rowDataMap[date] = { [category.id]: point }
        else rowDataMap[date][category.id] = point
      }
    )
  })

  const dateStrings = Object.keys(rowDataMap).filter((date) =>
    filterByDateRange(date, filterStartDate, filterEndDate)
  )

  const rowComponents = dateStrings
    .sort(
      (a, b) =>
        moment(a, MM_DD_YYYY).valueOf() - moment(b, MM_DD_YYYY).valueOf()
    )
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((date) => {
      const values = Object.values(rowDataMap[date]).map((point) => point)

      return (
        <EditChartDataTableRow
          chartId={chart.id}
          key={date}
          popToast={popToast}
          row={{ date, values }}
          goalId={goalId}
          simple={simple}
          onEditData={onEditData}
        />
      )
    })

  return (
    <EditChartDataTableContainer>
      <Table>
        <TableHead>
          <ChartTableRow>
            <ChartDateCell>Date</ChartDateCell>
            {categoryHeaders}
            <ChartTableCell align='center' width='15%'>
              Actions
            </ChartTableCell>
          </ChartTableRow>
        </TableHead>
        <TableBody>
          {isAddingData && (
            <CreateNewEntryRow
              newEntryDate={newEntryDate}
              newEntryValues={newEntryValues}
              onChangeDate={onChangeNewEntryDate}
              onCreateDataPoints={handleCreateDataPoints}
              onChangeValues={handleValueChange}
              onClose={handleCloseNewEntry}
              goalId={goalId}
            />
          )}
          {rowComponents}
        </TableBody>
      </Table>
      <TablePagination
        component='div'
        count={dateStrings.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={snackbarMessage}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
      />
    </EditChartDataTableContainer>
  )
}
